import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#loadSlidesEarly(signal);
  }

  /**
   * Silenxio: el HTML solo marca eager la primera foto del carrusel (imagen LCP). El resto pasa a eager
   * con lo que ocurra antes: la página termina de cargar (en un momento libre, máx. 2 s después) o el
   * cliente toca o desliza el carrusel. Así no compiten con la imagen principal ni salen en blanco al deslizar.
   * @param {AbortSignal} signal
   */
  #loadSlidesEarly(signal) {
    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    const listeners = new AbortController();
    signal.addEventListener('abort', () => listeners.abort());

    const loadSlides = () => {
      if (listeners.signal.aborted) return;
      listeners.abort();
      for (const image of slideshow.querySelectorAll('slideshow-slide img.product-media__image[loading="lazy"]')) {
        image.loading = 'eager';
      }
    };

    const options = { signal: listeners.signal, passive: true, capture: true };
    for (const type of ['touchstart', 'pointerdown', 'scroll']) {
      slideshow.addEventListener(type, loadSlides, options);
    }

    const loadWhenIdle = () => requestIdleCallback(loadSlides, { timeout: 2000 });
    if (document.readyState === 'complete') loadWhenIdle();
    else window.addEventListener('load', loadWhenIdle, { signal: listeners.signal, once: true });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
